/**
 * @module audit
 * @description Generic audit logging for modules and plugins
 */

import type { ModuleManifest, ModuleContext } from '@gzl10/nexus-sdk'
import { auditLogEntity } from './audit.entity.js'
import { createAuditService } from './audit.service.js'
import type { AuditEntry } from './audit.types.js'
import type { SchedulesApi } from '../schedules/index.js'

export type { AuditService, AuditEntry, AuditQuery, AuditLogRow } from './audit.types.js'
export { createAuditService } from './audit.service.js'

export const auditModule: ModuleManifest = {
  name: 'audit',
  label: { en: 'Audit', es: 'Auditoría' },
  icon: 'mdi:shield-search',
  description: {
    en: 'Generic audit logging for modules and plugins',
    es: 'Registro de auditoría genérico para módulos y plugins'
  },
  type: 'core',
  category: 'compliance',
  dependencies: ['logger'],

  definitions: [
    auditLogEntity
  ],

  routePrefix: '/audit',

  init: (ctx: ModuleContext) => {
    const auditService = createAuditService({
      knex: ctx.db.knex,
      generateId: ctx.core.generateId,
      nowTimestamp: ctx.db.nowTimestamp,
      logger: ctx.core.logger
    })
    ctx.services.register('audit', auditService)

    // Listen for audit events (decoupled — emitters don't need to know about audit module)
    ctx.events.on('audit.log', (entry) => {
      auditService.log(entry as AuditEntry)
    })

    ctx.events.on('schedules.ready', async (...args: unknown[]) => {
      const schedules = args[0] as SchedulesApi
      schedules.registerFunction('audit:cleanup', async () => {
        const cutoff = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString()
        const deleted = await ctx.db.knex('audit_log').where('created_at', '<', cutoff).delete()
        return { deleted, table: 'audit_log', cutoff }
      })
      await schedules.ensureSystemSchedule({
        name: '__system:cleanup-audit',
        description: 'Purge audit_log older than 90 days',
        cron_expression: '0 4 * * *',
        function_key: 'audit:cleanup'
      })
    })

    ctx.core.logger.debug('Audit service registered')
  }
}
